import { convertToCamelCase } from "../utils/caseUtils";
import api from "../utils/apiClient";
import axios from "axios";

const authAPIPrefix = "/api/auth/v1";
/**
 * Fetch All aar reporting marks
 * @returns {Promise<Array>} - The list of reporting marks.
 * @throws Will throw an error if the fetch fails.
 * @description Fetches all reporting marks from the API.
 */ 
export const fetchReportingMarks = async () => {
  try {
    const response = await api.get(`${authAPIPrefix}/reporting-marks`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch reporting marks:", error);
    throw new Error("Failed to fetch reporting marks");
  }
}


/**
 * saveAccount
 * @description Saves a new account.
 * @param {Object} accountData - The account data to save.
 */
export const saveAccount = async (accountData) => {
  try {
    await api.post(`${authAPIPrefix}/account`, accountData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to save account:", error);
    throw new Error("Failed to save account");
  }
};

/**
 * Fetches the user's profile data.
 * @param {string} memberId - The member ID.
 * @returns {Promise<Object>} - The user's profile data.
 */
export const fetchProfile = async (memberId) => {
  try {
    const response = await api.get(`${authAPIPrefix}/member/${memberId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch profile data:", error);
    throw new Error("Failed to fetch profile data");
  }
};

/**
 * Updates the user's profile data.
 * @param {Object} profileData - The profile data to update.
 * @returns {Promise<void>}
 */
export const updateProfile = async (profileData) => {
  try {
    await api.put(`${authAPIPrefix}/member/${profileData.memberId}`, profileData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to update profile:", error);
    throw new Error("Failed to update profile");
  }
};

/**
 * Fetches the list of users.
 * @param {string} acctId - The account ID.
 * @returns {Promise<Array>} - The list of users.
 */
export const fetchMembersNoCamel = async (acctId) => {
  try {
    const response = await api.get(`${authAPIPrefix}/members/${acctId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch members:", error);
    throw new Error("Failed to fetch members");
  }
};

/**
 * Fetches the list of users.
 * @param {string} acctId - The account ID.
 * @returns {Promise<Array>} - The list of users.
 */
export const fetchMembers = async (acctId) => {
  try {
    const response = await api.get(`${authAPIPrefix}/members/${acctId}`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch members:", error);
    throw new Error("Failed to fetch members");
  }
};

/**
 * Saves the member accounts.
 * @param {string} memberId - The ID of the member.
 * @param {Array} accounts - The accounts to save.
 * @returns {Promise<void>}
 */
export const saveMemberAccounts = async (memberId, accounts) => {
  // Check if accounts is an array and has at least one account
  if (accounts.length > 0) {
    try {
      await api.put(`${authAPIPrefix}/member/${memberId}/accounts`, 
        { accounts }, 
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      console.error("Failed to save member accounts and roles:", error);
      throw new Error("Failed to save member accounts and roles");
    }
  }
};

/**
 * Saves the member roles.
 * @param {string} token - The current authentication token.
 * @param {Function} updateToken - Function to update the token in AuthContext.
 * @param {string} memberId - The ID of the member.
 * @param {Array} roles - The roles to save.
 * @returns {Promise<void>}
 */
/**
 * Saves the member roles.
 * @param {string} subscrId - The subscription ID.
 * @param {string} memberId - The ID of the member.
 * @param {string} roleId - The role ID.
 * @returns {Promise<void>}
 */
export const saveAccountMemberRoles = async (subscrId, memberId, roleId) => {
  try {
    await api.put(`${authAPIPrefix}/subscr/${subscrId}/member/${memberId}/role/${roleId}`, {}, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to save member roles:", error);
    throw new Error("Failed to save member roles");
  }
};

/**
 * Fetches the list of roles.
 * @returns {Promise<Array>} - The list of roles.
 */
export const fetchRoles = async () => {
  try {
    const response = await api.get(`${authAPIPrefix}/roles`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch roles:", error);
    throw new Error("Failed to fetch roles");
  }
};

/**
 * Saves a new role.
 * @param {string} roleCode - The role code to save.
 * @returns {Promise<void>}
 */
export const saveRoles = async (roleCode) => {
  try {
    await api.post(`${authAPIPrefix}/role`, roleCode, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to save role:", error);
    throw new Error("Failed to save role");
  }
};

/**
 * Updates a role.
 * @param {string} roleId - The ID of the role to update.
 * @param {string} roleCode - The new role code.
 * @returns {Promise<void>}
 */
export const updateRole = async (roleId, roleCode) => {
  try {
    await api.put(`${authAPIPrefix}/role/${roleId}`, roleCode, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to update role:", error);
    throw new Error("Failed to update role");
  }
};
/**
 * Deletes a role.
 * @param {string} roleId - The ID of the role to delete.
 * @returns {Promise<void>}
 */
export const deleteRole = async (roleId) => {
  try {
    await api.delete(`${authAPIPrefix}/role/${roleId}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to delete role:", error);
    throw new Error("Failed to delete role");
  }
};

export const sendMemberInvitations = async (invitations) => {
  try {
    const response = await api.post("/api/auth/v1/member/invitations", 
      { invitations }, 
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to send member invitations:", error);
    throw new Error("Failed to send member invitations");
  }
}

export const resendMemberInvitation = async (invitationId) => {
  try {
    const response = await api.post(`${authAPIPrefix}/member/invitation/${invitationId}/resend`, 
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to resend member invitation:", error);
    throw new Error("Failed to resend member invitation");
  }
}

export const ValidateMemberInvitation = async (token) => {
  // Validate token before sending request
  if (!token) {
    throw new Error("Token is required");
  }
  try {      
    const response = await api.post(`${authAPIPrefix}/member/validate-invitation-token`, 
      { token }, 
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Token validation error:", error);
    throw error;
  }
};

export async function RegisterExistingMember(token) {
  try {
    const response = await api.post(`${authAPIPrefix}/member/register-existing-member`, 
      { token }, 
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Registration error:', error);
    throw new Error('Registration failed. Please try again.');
  }
}

/**
 * Sends a password reset email.
 * @param {string} email - The email address to send the reset link to.
 * @returns {Promise<void>}
 * @throws Will throw an error if the fetch fails.
 * @description Sends a password reset email to the specified address.
 */
export const sendPasswordResetEmail = async (email) => {
  if (!email) {
    throw new Error("Email is required");
  }
  if (!/\S+@\S+\.\S+/.test(email)) {
    throw new Error("Invalid email format");
  }
  try {
    const response = await api.post(`${authAPIPrefix}/forgot-password`, 
      { email }, 
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes("application/json")) {
      return response.data;
    } else {
      return { message: response.data }; // Handle plain text response
    }
  } catch (error) {
    console.error("Failed to send password reset email:", error);
    throw new Error("Failed to send password reset email");
  }
}

/**Handle the password reset link
 * @param {string} token - The reset token.
 * @returns {Promise<void>}
 * @throws Will throw an error if the fetch fails.
 * @description Handles the password reset link by verifying the token.
 */
export const ValidatePasswordResetToken = async (token) => {
  try {
    const response = await api.post(`${authAPIPrefix}/validate-reset-token`, 
      { token }, 
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to verify reset token:", error);
    throw new Error("Failed to verify reset token");
  }
};

/**
 * Resets the password.
 * @param {string} token - The reset token.
 * @param {string} newPassword - The new password.
 * @returns {Promise<object>} - The response from the server
 * @throws Will throw an error if the fetch fails.
 */
export const resetPassword = async (token, newPassword) => {
  if (!token) {
    console.error("No token provided to resetPassword function");
    throw new Error("Token is required");
  }
  
  if (!newPassword) {
    throw new Error("New password is required");
  }
  
  try {
    const response = await api.post(`${authAPIPrefix}/reset-password`, 
      { token, newPassword }, 
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Password reset error:", error);
    throw error instanceof Error ? error : new Error("Failed to reset password");
  }
}

export const fetchRailroadOperators = async () => {
  try {
    const response = await api.get(`${authAPIPrefix}/railroad-operators`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch railroad operators:", error);
    throw new Error("Failed to fetch railroad operators");
  }
}

/**
 * Fetch all portals 
 * @returns {Promise<Array>} - The list of portals.
 * @throws Will throw an error if the fetch fails.
 * @description Fetches all portals from the API.
 */
export const fetchPortals = async () => {
  try {
    const response = await api.get(`${authAPIPrefix}/portals`);
    return convertToCamelCase(response.data);
  } catch (error) {
    console.error("Failed to fetch portals:", error);
    throw new Error("Failed to fetch portals");
  }
}

/**
 * Updates camera settings for a member
 * @param {string} memberId - Member ID
 * @param {Array} settings - Camera settings array
 * @param {string} group - Settings group (e.g., 'default')
 * @param {string} site - Site identifier
 * @param {Array} cameraRowOrder - Optional array specifying the order of main cameras
 * @param {Array} areascanCameraRowOrder - Optional array specifying the order of areascan cameras
 * @returns {Promise<Object>} - Response from API
 */
export const updateCameraSettings = async (memberId, settings, group, site, cameraRowOrder = [], areascanCameraRowOrder = []) => {
  // Prepare request body with settings and optional camera row orders
  const requestBody = {
    settings: settings,
  };
  
  // Only include cameraRowOrder if it exists and has content
  if (cameraRowOrder && cameraRowOrder.length > 0) {
    requestBody.cameraRowOrder = cameraRowOrder;
  }
  
  // Include areascanCameraRowOrder if it exists and has content
  if (areascanCameraRowOrder && areascanCameraRowOrder.length > 0) {
    requestBody.areascanCameraRowOrder = areascanCameraRowOrder;
  }
  
  try {
    const response = await api.put(`${authAPIPrefix}/member/${memberId}/camera-settings/${group}/${site}`, 
      settings, 
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update camera settings:', error);
    throw new Error('Failed to update camera settings');
  }
};

// Add a member directly (without invitation)
export const addDirectMember = async (memberData) => {
  try {
    const response = await api.post(`${authAPIPrefix}/member/${memberData.account_id}/members`, 
      memberData, 
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding member:", error);
    throw new Error("Failed to add member");
  }
};

export const updateMemberAccount = async (memberID, acctID, acctMemberData) => {
  try {
    await api.put(`${authAPIPrefix}/member/${memberID}/${acctID}`, acctMemberData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to update member account data:", error);
    throw new Error("Failed to update member account data");
  }
};

// ============================================================================
// Unauthenticated API Functions
// These use axios directly instead of the configured api client
// because they need to work before authentication is established
// ============================================================================

/**
 * Login with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {string} mfaCode - Optional MFA code
 * @returns {Promise<Object>} - Login response with tokens and MFA status
 */
export const login = async (email, password, mfaCode = null) => {
  try {
    const response = await axios.post(`${authAPIPrefix}/login`, {
      email,
      password,
      mfaCode,
    });
    return response.data;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

/**
 * Register a new member
 * @param {Object} registrationData - Registration data
 * @returns {Promise<Object>} - Registration response
 */
export const registerMember = async (registrationData) => {
  try {
    const response = await axios.post(`${authAPIPrefix}/member/member-register`, registrationData);
    return response.data;
  } catch (error) {
    console.error("Registration failed:", error);
    throw error;
  }
};

/**
 * Resend MFA code
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} - Resend MFA response
 */
export const resendMFA = async (email, password) => {
  try {
    const response = await axios.post(`${authAPIPrefix}/resend-mfa`, {
      email,
      password,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to resend MFA:", error);
    throw error;
  }
};

/**
 * Refresh authentication tokens
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<Object>} - New tokens
 */
export const refreshAuthTokens = async (refreshToken) => {
  try {
    const response = await axios.post(`${authAPIPrefix}/refresh-token`, {
      refresh_token: refreshToken,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to refresh tokens:", error);
    throw error;
  }
};

/**
 * Fetch member profile with a specific token (for login flow)
 * @param {string} memberId - Member ID
 * @param {string} token - Access token
 * @returns {Promise<Object>} - Member profile data
 */
export const fetchProfileWithToken = async (memberId, token) => {
  try {
    const response = await axios.get(`${authAPIPrefix}/member/${memberId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch profile data:", error);
    // Check for 403 Forbidden (no accounts)
    if (error.response?.status === 403) {
      throw new Error("No accounts to log in to");
    }
    throw new Error("Failed to fetch profile data");
  }
};